import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface Integration {
  name: string;
  config: Record<string, any>;
  lookups: any[];
}

interface IntegrationsState {
  items: Integration[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

// Central TTL for integration lookups cache (default: 2 days)
export const INTEGRATIONS_CACHE_TTL_MS = 2 * 24 * 60 * 60 * 1000;;

const initialState: IntegrationsState = {
  items: [],
  status: "idle",
  error: null,
};

const integrationsSlice = createSlice({
  name: "integrations",
  initialState,
  reducers: {
    setLoading: (state) => {
      state.status = "loading";
      state.error = null;
    },
    setIntegrations: (state, action: PayloadAction<string[]>) => {
      state.status = "succeeded";
      const existingItems = state.items || [];
    
      state.items = action.payload.map((name) => {
        const existing = existingItems.find((i) => i.name === name);
        return {
          name,
          config: existing?.config ?? {},
          lookups: existing?.lookups ?? [], // 🔑 PRESERVE lookups if they exist
        };
      });
    },
    setIntegrationConfig: (state, action: PayloadAction<{ name: string; config: Record<string, any> }>) => {
      const index = state.items.findIndex((item) => item.name === action.payload.name);
      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          config: action.payload.config
        };
      }
    },
    setIntegrationLookups: (state, action: PayloadAction<{ name: string; lookups: any[] | any }>) => {
      const index = state.items.findIndex((item) => item.name === action.payload.name);
      if (index !== -1) {
        if (Array.isArray(action.payload.lookups)) {
          action.payload.lookups.forEach((lookup) => {
            state.items[index].lookups.push(lookup);
          });
        } else {
          state.items[index].lookups.push(action.payload.lookups);
        }
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.status = "failed";
      state.error = action.payload;
    },
    reset: (state) => {
      state.status = "idle";
      state.error = null;
      state.items = [];
    }
  }
});

export const { setLoading, setIntegrations, setIntegrationConfig, setIntegrationLookups, setError, reset } = integrationsSlice.actions;
export default integrationsSlice.reducer;
